import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  ApiError,
  createEmployee,
  type EmployeeDetail,
  fetchBusinessLines,
  fetchDepartments,
  searchEmployees,
} from './api'

export default function EmployeeCreatePanel({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (employeeId: number) => void
}) {
  const queryClient = useQueryClient()

  const [fullName, setFullName] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [lineId, setLineId] = useState('')
  const [role, setRole] = useState('')
  const [hiredAt, setHiredAt] = useState('')
  const [inManagement, setInManagement] = useState(true)
  const [managerQuery, setManagerQuery] = useState('')
  const [managerId, setManagerId] = useState<number | null>(null)

  // Показывается после успешного создания, пока администратор не закроет панель.
  const [result, setResult] = useState<EmployeeDetail | null>(null)

  const departments = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })
  const lines = useQuery({ queryKey: ['business-lines'], queryFn: fetchBusinessLines })

  const managerOptions = useQuery({
    queryKey: ['manager-search', managerQuery],
    queryFn: () => searchEmployees(managerQuery),
    enabled: managerQuery.trim().length >= 2,
  })

  const save = useMutation({
    mutationFn: () =>
      createEmployee({
        full_name: fullName,
        department_id: departmentId ? Number(departmentId) : null,
        manager_id: managerId,
        role: role || null,
        business_line_id: lineId ? Number(lineId) : null,
        hired_at: hiredAt || null,
        in_management_structure: inManagement,
      }),
    onSuccess: (created) => {
      setResult(created)
      queryClient.invalidateQueries({ queryKey: ['chart'] })
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  if (result) {
    return (
      <aside className="side-panel">
        <div className="side-panel-head">
          <strong>Сотрудник добавлен</strong>
          <button type="button" className="link" onClick={onClose}>
            Закрыть
          </button>
        </div>

        <p>{result.display_name}</p>

        <div className="actions-left">
          <button type="button" onClick={() => onCreated(result.id)}>
            Открыть карточку
          </button>
          <button
            type="button"
            onClick={() => {
              setResult(null)
              setFullName('')
              setManagerQuery('')
              setManagerId(null)
              setRole('')
            }}
          >
            Добавить ещё
          </button>
        </div>
      </aside>
    )
  }

  return (
    <aside className="side-panel">
      <div className="side-panel-head">
        <strong>Новый сотрудник</strong>
        <button type="button" className="link" onClick={onClose}>
          Закрыть
        </button>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <label className="field">
          <span>ФИО</span>
          <input
            required
            autoFocus
            value={fullName}
            placeholder="Иванов Иван Иванович"
            onChange={(event) => setFullName(event.target.value)}
          />
        </label>
        <p className="muted small">
          Одной строкой. Отчество не обязательно — в справочнике есть и двухсловные имена.
        </p>

        <label className="field">
          <span>Подразделение</span>
          <select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
            <option value="">—</option>
            {(departments.data ?? []).map((department) => (
              <option key={department.id} value={department.id}>
                {department.code}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Направление</span>
          <select value={lineId} onChange={(event) => setLineId(event.target.value)}>
            <option value="">—</option>
            {(lines.data ?? []).map((line) => (
              <option key={line.id} value={line.id}>
                {line.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Роль</span>
          <input value={role} onChange={(event) => setRole(event.target.value)} />
        </label>

        <label className="field">
          <span>Дата выхода на работу</span>
          <input
            type="date"
            value={hiredAt}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(event) => setHiredAt(event.target.value)}
          />
        </label>
        <p className="muted small">
          Можно не заполнять — карточку заводят и на давно работающего. Пустая дата
          означает «неизвестна», а не сегодня.
        </p>

        <label className="field">
          <span>Руководитель</span>
          <input
            value={managerQuery}
            placeholder="Начните вводить имя…"
            onChange={(event) => {
              setManagerQuery(event.target.value)
              setManagerId(null)
            }}
          />
        </label>
        {managerOptions.data && managerQuery.trim().length >= 2 && managerId === null && (
          <ul className="search-results inline">
            {managerOptions.data.length === 0 && <li className="muted">Ничего не найдено</li>}
            {managerOptions.data.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setManagerId(candidate.id)
                    setManagerQuery(candidate.display_name)
                  }}
                >
                  {candidate.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}

        <label className="toggle">
          <input
            type="checkbox"
            checked={inManagement}
            onChange={(event) => setInManagement(event.target.checked)}
          />
          В активной структуре управления
        </label>

        {error && <p className="bad form-error">{error}</p>}

        <div className="actions-left">
          <button type="submit" disabled={save.isPending || !fullName.trim()}>
            {save.isPending ? 'Создаём…' : 'Создать'}
          </button>
          <button type="button" onClick={onClose}>
            Отмена
          </button>
        </div>
      </form>
    </aside>
  )
}
